import * as React from 'react';
import { useLocation, NavLink } from 'react-router-dom';

import { phoneFormat } from '@common/react/components/Forms/FormikPhoneControl/FormikPhoneControl';

import { SocialMedia as SocialMediaType } from '@commonTuna/react/objects/SocialMedia';

import { useReduxContext } from '@app/hooks/useReduxContext';
import SocialMedia from '@app/components/UI/SocialMedia/SocialMedia';

interface PassedProps {
	phone?: string;
	socialMedias?: Array<SocialMediaType & { title, icon }>;
}

type HeaderProps = PassedProps;

const Header: React.FC<HeaderProps> = ({ socialMedias: propsSocialMedias, phone: propsPhone }) => {
	const [isMenuOpen, setOpen] = React.useState(false);
	const toggleMenu = React.useCallback(() => setOpen((prev) => !prev), []);
	const close = React.useCallback(() => setOpen(false), []);
	const browserLocation = useLocation();

	const { blogPageId, location, menu: storeMenu } = useReduxContext();

	const mainMenu = React.useMemo(() => {
		const menu = [
			{
				path: '/#home', name: 'Home', basePath: '', fullPath: '',
			},
			{
				path: '/#info',
				name: 'About',
				basePath: '',
				fullPath: '',
				children: [
					{
						path: '/about',
						name: 'Meet the team',
						fullPath: '',
					},
				],
			},
			{
				path: '/#booking', name: 'Schedule', basePath: '', fullPath: '', className: 'show-mobile',
			},
			{
				path: '/#services',
				name: 'Services',
				basePath: '',
				fullPath: '',
				children: storeMenu.map((page) => ({
					path: `${page.fullPath.replace(/^\//, '')}`,
					name: page.shortNameEn,
					fullPath: '',
				})),
			},
			{
				path: '/#blog', name: 'Blog', basePath: '', fullPath: '',
			},
			{
				path: '/#faq', name: 'FAQ', basePath: '', fullPath: '',
			},
			{
				path: '/#reviews', name: 'Reviews', basePath: '', fullPath: '',
			},
			{
				path: '/gallery', name: 'Gallery', basePath: '', fullPath: '',
			},
			{
				path: '/#contacts', name: 'Contacts', basePath: '', fullPath: '',
			},
		];

		return menu.filter((item) => item.path !== '#blog' || blogPageId > 0);
	}, [blogPageId, browserLocation.pathname]);

	const socialMedias = propsSocialMedias || location.socialMedias;
	const phone = propsPhone || location.phone;

	return <header className="site-header">
		<nav className="navbar navbar-default navbar-fixed-top">
			<div className="container">
				<NavLink to="/" className="logo-wrapper" aria-label="logo">
					<picture>
						<source media="(min-width: 769px)" srcSet="/images/SM.webp" />
						<img alt={`${location.companyName} Logo`} className="header-logo" width="124px" height="80px" srcSet="/images/SM.webp" />
					</picture>
				</NavLink>
				<div className="navbar-left pull-left">
					<div className="navbar-burger__container">
						<button
							type="button"
							className={`btn navbar-burger ${isMenuOpen ? 'navbar-burger__open' : ''}`}
							aria-label="open sidebar"
							onClick={toggleMenu}
						>
							<span />
						</button>
					</div>
					<ul className={`menu-component menu-component${isMenuOpen ? '__open' : '__close'}`}>
						{mainMenu.map((item, i) =>
							<li key={`${item.name}${item.path}`} className={`menu-component__item ${item.className ? item.className : ''}`}>
								<NavLink
									onClick={close}
									to={item.basePath + item.path}
									className="menu-component__item-link"
								>
									{item.name}
								</NavLink>
								{item.children?.length ? <div className="sub-menu">
									<ul>
										{item.children.map((item, i) => <li
											key={`${item.name}${item.path}`}
											className="menu-component__item"
										>
											<NavLink
												onClick={close}
												to={item.path}
												className="menu-component__item-link"
											>
												{item.name}
											</NavLink>
										</li>)}
									</ul>
								</div> : null}
							</li>)
						}
						{socialMedias ? <li key="socialMedias" className="social-medias show-mobile">
							<SocialMedia socialMedias={socialMedias} />
						</li> : null}
					</ul>
					<div className="background" />
				</div>
				<div className="pull-right navbar-right">
					{socialMedias ? <SocialMedia socialMedias={socialMedias} /> : null}
					{phone
						&& <a
							className="phone-link"
							href={`tel:${phone}`}
							key="phone"
							title={`Call ${location.companyName} Clinic`}
						>
							<i className="fa fa-phone" style={{ marginRight: 8 }} />
							<span className="phone-number">{phoneFormat(phone)}</span>
						</a>
					}
				</div>
			</div>
		</nav>
	</header>;
};

export default Header;
